import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { Product } from '../types';
import { useAuth } from './AuthContext';
import { macchineService, SavedMachine } from '../services/macchineService';
import { eContatto } from '../utils/contatto';

// ============================================
// Favorites Context
// ============================================
interface FavoritesContextValue {
  favorites: Product[];
  addFavorite: (product: Product) => void;
  removeFavorite: (sku: string) => void;
  isFavorite: (sku: string) => boolean;
  toggleFavorite: (product: Product) => void;
}

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [favorites, setFavorites] = useState<Product[]>(() => {
    try { const saved = localStorage.getItem('dtk_favorites'); return saved ? JSON.parse(saved) : []; } catch { return []; }
  });

  useEffect(() => { localStorage.setItem('dtk_favorites', JSON.stringify(favorites)); }, [favorites]);

  const addFavorite = useCallback((product: Product) => {
    setFavorites(prev => prev.some(f => f.sku === product.sku) ? prev : [...prev, product]);
  }, []);

  const removeFavorite = useCallback((sku: string) => {
    setFavorites(prev => prev.filter(f => f.sku !== sku));
  }, []);

  const isFavorite = useCallback((sku: string) => favorites.some(f => f.sku === sku), [favorites]);

  const toggleFavorite = useCallback((product: Product) => {
    setFavorites(prev => prev.some(f => f.sku === product.sku)
      ? prev.filter(f => f.sku !== product.sku)
      : [...prev, product]
    );
  }, []);

  return (
    <FavoritesContext.Provider value={{ favorites, addFavorite, removeFavorite, isFavorite, toggleFavorite }}>
      {children}
    </FavoritesContext.Provider>
  );
}

export function useFavorites() {
  const ctx = useContext(FavoritesContext);
  if (!ctx) throw new Error('useFavorites must be used within FavoritesProvider');
  return ctx;
}

// ============================================
// My Machines Context — Parco Macchine (dati reali dal backend)
// ============================================
interface MachinesContextValue {
  machines: SavedMachine[];
  loading: boolean;
  addMachine: (modelloId: string) => Promise<void>;
  removeMachine: (id: string) => Promise<void>;
  hasMachine: (modelloId: string) => boolean;
}

const MachinesContext = createContext<MachinesContextValue | null>(null);

/** Marca poi nome, come li rende il backend: un modello aggiunto va al suo posto. */
function sortMachines(items: SavedMachine[]): SavedMachine[] {
  return [...items].sort((a, b) => a.marca.localeCompare(b.marca) || a.nome.localeCompare(b.nome));
}

export function MachinesProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, hasRole, user } = useAuth();
  const [machines, setMachines] = useState<SavedMachine[]>([]);
  const [loading, setLoading] = useState(false);

  // I modelli sono legati al conto NTS: un Contatto non ne ha (404) finché
  // il primo ordine non crea il Cliente.
  const canUseMachines = isAuthenticated && hasRole('cliente', 'cliente_subordinato') && !eContatto(user);

  useEffect(() => {
    if (!canUseMachines) { setMachines([]); return; }
    let cancelled = false;
    setLoading(true);
    macchineService.list()
      .then(items => { if (!cancelled) setMachines(items); })
      .catch(() => { if (!cancelled) setMachines([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canUseMachines]);

  const addMachine = useCallback(async (modelloId: string) => {
    const saved = await macchineService.add(modelloId);
    setMachines(prev => (prev.some(m => m.modelloId === saved.modelloId) ? prev : sortMachines([...prev, saved])));
  }, []);

  const removeMachine = useCallback(async (id: string) => {
    await macchineService.remove(id);
    setMachines(prev => prev.filter(m => m.id !== id));
  }, []);

  const hasMachine = useCallback((modelloId: string) => machines.some(m => m.modelloId === modelloId), [machines]);

  return (
    <MachinesContext.Provider value={{ machines, loading, addMachine, removeMachine, hasMachine }}>
      {children}
    </MachinesContext.Provider>
  );
}

export function useMachines() {
  const ctx = useContext(MachinesContext);
  if (!ctx) throw new Error('useMachines must be used within MachinesProvider');
  return ctx;
}
